import { format } from '@rental/i18n'
import { Button, Notice } from '@rental/ui'
import { type ReactNode, useEffect } from 'react'
import brandDisc from '../assets/brand-disc.webp'
import { KeptOnIpad } from '../capture/CaptureUi'
import { useLocale } from '../i18n'
import { onResume } from '../resume'

/**
 * The other language. In the shell a link among the person's things; before
 * the PIN a button of its own in the screen's corner (UI-2a, the owner's
 * mock-up: a soft rectangle, the faint rule for its edge), as tall as a finger.
 */
export function LanguageSwitch({ pill = false }: { pill?: boolean }) {
  const { locale, setLocale, messages } = useLocale()
  const other = locale === 'it' ? 'en' : 'it'
  if (pill) {
    return (
      <button
        type="button"
        lang={other}
        aria-label={messages.nav.otherLanguageLabel}
        onClick={() => setLocale(other)}
        className="inline-flex min-h-touch items-center rounded-sm border border-rule bg-surface-pane px-4 text-sm font-medium text-brand-strong transition-colors hover:text-link"
      >
        {messages.nav.otherLanguage}
      </button>
    )
  }
  return (
    <Button
      variant="link"
      lang={other}
      aria-label={messages.nav.otherLanguageLabel}
      onClick={() => setLocale(other)}
    >
      {messages.nav.otherLanguage}
    </Button>
  )
}

/**
 * The company's round M, its name, and under it what this is: the device on
 * the lock screen, "Staff" elsewhere. The disc is the site's own icon, bundled
 * (UI-2a; the owner confirmed the logo is the company's, 19 Oct). The name
 * follows it, so the picture says nothing of its own.
 */
export function Brand({ line }: { line: ReactNode }) {
  const { messages } = useLocale()
  return (
    <div className="flex items-center gap-3">
      <img src={brandDisc} alt="" className="size-8 shrink-0" />
      <div className="flex min-w-0 flex-col">
        <p className="text-sm leading-heading font-bold text-brand-strong">{messages.app.name}</p>
        <p className="text-xs text-text-muted">{line}</p>
      </div>
    </div>
  )
}

/** The shell's brand, at the top of the sidebar and of the menu's sheet. */
export function Wordmark() {
  const { messages } = useLocale()
  return <Brand line={messages.app.product} />
}

/**
 * The frame of the screens before anyone is unlocked: registering the device,
 * the lock screen, the password sign-in, and the app starting or failing to.
 * One card over the office at the port (DESIGN.md, "Staff app direction"):
 * every word is on it, or on a pane of its own. From 1024 px the language
 * stands in the top right corner and `aside` in the top left, out of the
 * card's height; below, the language comes before the card and `aside` after
 * it, as it is read (UI-2g). `device` names the iPad on the
 * lock screen, where whoever unlocks needs to know which one it is.
 */
export function Frame({
  children,
  device,
  aside,
}: {
  children: ReactNode
  device?: string
  aside?: ReactNode
}) {
  const { messages: m } = useLocale()
  return (
    <>
      <div className="depth depth-office" aria-hidden="true" />
      <a href="#main" className="sr-only focus:not-sr-only focus:bg-surface focus:p-4">
        {m.app.skipToContent}
      </a>
      <main
        id="main"
        tabIndex={-1}
        className="flex min-h-dvh w-full flex-col items-center justify-center gap-4 p-4 lg:py-2"
      >
        <div className="flex w-full max-w-md justify-end lg:fixed lg:end-8 lg:top-8 lg:w-auto">
          <LanguageSwitch pill />
        </div>
        <div className="frame-card glass-pane flex w-full max-w-md flex-col gap-2 rounded-lg px-8 py-6">
          <Brand
            line={
              device ? (
                <>
                  <span className="sr-only">{format(m.lock.device, { name: device })}</span>
                  <span aria-hidden="true">{device}</span>
                </>
              ) : (
                m.app.product
              )
            }
          />
          {children}
        </div>
        {/* After the card, in what is read as in what is seen below 1024 px: the card's
            title takes the focus, and what came before it was never reached reading on
            (UI-2g). From 1024 px it stands in the top left corner, where it always was. */}
        {aside ? (
          <div className="flex w-full max-w-md flex-col gap-3 lg:fixed lg:start-8 lg:top-8 lg:w-auto lg:max-w-3xs">
            {aside}
          </div>
        ) : null}
      </main>
    </>
  )
}

export function Splash() {
  const { messages } = useLocale()
  return (
    <Frame>
      <p role="status" className="py-8 text-center text-text-muted">
        {messages.app.loading}
      </p>
    </Frame>
  )
}

export function BootProblem({ retry, message }: { retry: () => void; message: string }) {
  const { messages } = useLocale()
  // S4: the connection back, or the iPad back in front: try again without being asked.
  useEffect(() => {
    window.addEventListener('online', retry)
    const stopResume = onResume(retry)
    return () => {
      window.removeEventListener('online', retry)
      stopResume()
    }
  }, [retry])
  return (
    <Frame>
      <Notice tone="danger" live="assertive">
        <p>{message}</p>
      </Notice>
      <KeptOnIpad />
      <Button variant="staff" block className="shadow-action" onClick={retry}>
        {messages.app.retry}
      </Button>
    </Frame>
  )
}
